// src/pages/Courses.jsx
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import "../styles/courses.css";


/* ================= MODULE DATA ================= */
const modules = [
  {
    id: 1,
    title: "Accounting",
    subtitle: "Core accounting & book keeping",
    shortDesc: "Covers day-to-day accounting processes including vouchers, invoicing, reconciliation, depreciation, and inventory. Learners understand P&L, Balance Sheet, Cash Flow, and finalization of books. Builds strong practical knowledge for accounting and finance roles.",
    cardImage: "/images/accounting.png",
    details: [
      "Concept of Accounting",
      "Voucher Preparation",
      "Invoicing — Debit & Credit Note",
      "Ageing — Creditor, Debtor & Inventory",
      "Bank Reconciliation",
      "Ledger Reconciliation",
      "Depreciation",
      "Fixed Asset Register — Company Act & Income Tax Act",
      "Cash Flow and Fund Flow",
      "Understanding P/L, Balance Sheet & Audit Report",
      "Books Finalization",
      "Purchase Order, Sales Order (Quotation)",
      "Cheque & Payment Advice Preparation",
      "Inventory Management",
      "Ratio Analysis",
      "Reconciliation Process — KPO",
      "Accounting Process — KPO",
      "Invoicing Process — KPO",
      "Accounts Payable — KPO",
    ],
  },
  {
    id: 2,
    title: "Income Tax",
    subtitle: "Income tax & compliance",
    shortDesc:"Provides complete knowledge of India’s Income Tax system under the Income Tax Act, 1961. Includes tax computation, returns filing, TDS, MAT, deductions, and tax planning. Prepares learners to handle income tax compliance independently.",
    cardImage: "/images/incometax.png",
    details: [
      "Overview of direct taxation system of India",
      "Basic concepts and definitions (Income Tax Act, 1961)",
      "Residential status & obtaining PAN",
      "Registration under Income Tax",
      "Incomes exempt from tax",
      "Income from salaries, house property, business/profession",
      "Capital gains & income from other sources",
      "Deductions from gross total income",
      "Clubbing of income; set off & carry forward of losses",
      "Return of income & assessment",
      "Advance tax calculation and online payment",
      "Computation of taxable income (individuals & companies)",
      "Minimum Alternate Tax (MAT)",
      "Revisions, defective returns and appeals",
      "Deferred tax; penalty & interest",
      "Tax planning and practical case studies",
      "TDS / TCS basics, Form 16, e-TDS returns",
    ],
  },
  {
    id: 3,
    title: "Tally & GST",
    subtitle: "Tally accounting & GST practicals",
    shortDesc:"Focuses on practical GST compliance with hands-on Tally ERP training. Covers registration, returns, ITC reconciliation, e-invoicing, and audits. Ideal for roles in GST and accounting operations.",
    cardImage: "/images/tally&gst.png",
    details: [
      "Constitution & definitions under GST",
      "Levy & supply under GST",
      "Time of supply, valuation & place of supply",
      "Input Tax Credit (eligibility, reconciliation, blockage & reversal)",
      "Registration — cancellation & revocation",
      "Return filing: composite, monthly, QRMP",
      "Form & return filing, computation & payment",
      "Practical return reconciliation & correction",
      "Invoicing, e-invoicing, HSN, job-work & e-way bill",
      "PMT-9, DRC-03 & notice handling",
      "Refund under GST; annual return & linking GSTR-1/GSTR-3B",
      "GST audit, advance ruling, assessments",
      "Inspection, search & seizure, show cause notices",
      "GST on stock transfer & work contractors",
      "Generating mismatch reports; penalty & interest",
    ],
  },
  {
    id: 4,
    title: "MS Excel & Reporting",
    subtitle: "Excel for accounting & analysis",
    shortDesc:"Covers essential Excel tools used in accounting and reporting. Includes formulas, pivot tables, dashboards, and MIS reports. Enhances accuracy, speed, and productivity at work.",
    cardImage: "/images/msexcel.png",
    details: [
      "Basic to advanced Excel",
      "Pivot tables",
      "VLOOKUP / XLOOKUP",
      "Financial reporting templates",
      "MIS reporting & dashboards",
    ],
  },
  {
    id: 5,
    title: "Interview Skills",
    subtitle: "Personality & interviews",
    shortDesc:"Improves English communication and interview confidence. Covers presentation skills, teamwork, leadership, and stress management. Prepares learners for professional workplace success.",
    cardImage: "/images/interview.png",
    details: [
      "Resume preparation (practical templates)",
      "Communication & presentation skills",
      "Mock interviews (technical & HR rounds)",
      "Group discussion techniques",
      "Confidence building & body language",
      "Behavioural question practice (STAR method)",
      "Professional etiquette & workplace communication",
    ],
  },
  {
    id: 6,
    title: "Banking & Audit",
    subtitle: "Banking, ESI, PF & audit basics",
    shortDesc:"Covers practical banking operations, audits, and company law compliance in one module. Includes online banking, NEFT/RTGS, loans, statutory & tax audits, and MCA filings. Prepares learners for real-world corporate finance, compliance, and accounting roles.",
    cardImage: "/images/banking&audit.png",
    details: [
      "Banking basics & reconciliation processes",
      "ESI & PF compliance overview",
      "Audit basics: types, scope & reporting",
      "Internal controls and statutory registers",
      "Cheque & payment advice preparation processes",
      "Practical bank audit examples",
      "Practical ledger audit examples",
    ],
  },
];

/* ================= COMPONENT ================= */
export default function Courses() {
  const navigate = useNavigate();
  const { id } = useParams();

  /* ========== COURSE DETAIL PAGE ========== */
  if (id) {
    const course = modules.find((c) => c.id === Number(id));
    if (!course) return null;

    return (
      <main className="courses-section">
        <div className="container">

          <button className="btn btn-ghost" onClick={() => navigate("/courses")}>
            ← Back to Courses
          </button>

          <h1 style={{ marginTop: "20px" }}>{course.title}</h1>
          <p className="muted">{course.subtitle}</p>

          <h3>Syllabus & Topics</h3>
          <ul className="topics-list">
            {course.details.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
          {/* ================= ACCOUNTING DETAILED CONTENT ================= */}
  {course.title === "Accounting" && (
    <>
      <h4 style={{ marginTop: "1.5rem" }}>Topic Explanation</h4>

      <div className="topic-explain">
        <p><strong>Concept of Accounting:</strong> Accounting is the process of recording, classifying, and summarizing financial transactions. It helps measure business performance and supports decision-making.</p>

        <p><strong>Voucher Preparation:</strong> Voucher preparation means creating proof for each transaction with details like date, amount, accounts, and supporting bills. It ensures entries are correct and authorized.</p>

        <p><strong>Invoicing — Debit & Credit Note:</strong> Invoicing is issuing a bill for goods or services sold. Debit and credit notes adjust invoices for returns, discounts, or extra charges.</p>

        <p><strong>Ageing — Creditor, Debtor & Inventory:</strong> Ageing shows how long receivables, payables, and inventory are pending. It helps control cash flow and stock movement.</p>

        <p><strong>Bank Reconciliation:</strong> Matches company cash book with bank statements to identify differences like bank charges or pending cheques.</p>

        <p><strong>Ledger Reconciliation:</strong> Confirms ledger balances with supporting documents to detect errors or missing entries.</p>

        <p><strong>Depreciation:</strong> Systematic reduction in asset value over its useful life.</p>

        <p><strong>Fixed Asset Register:</strong> Records asset purchase, location, and depreciation as per Company Act and Income Tax Act.</p>

        <p><strong>Cash Flow & Fund Flow:</strong> Cash flow tracks actual cash movement, while fund flow explains changes in working capital.</p>

        <p><strong>P/L, Balance Sheet & Audit Report:</strong> Financial statements showing profit, financial position, and audit reliability.</p>

        <p><strong>Books Finalization:</strong> Closing accounts with adjustments to prepare final financial statements.</p>

        <p><strong>Purchase & Sales Orders:</strong> Documents defining pricing, terms, and order confirmation.</p>

        <p><strong>Cheque & Payment Advice:</strong> Ensures correct payment execution and invoice-wise vendor communication.</p>

        <p><strong>Inventory Management:</strong> Controls stock levels, valuation, and wastage.</p>

        <p><strong>Ratio Analysis:</strong> Evaluates liquidity, profitability, efficiency, and solvency.</p>

        <p><strong>KPO Processes:</strong> Covers reconciliation, accounting workflows, invoicing, and accounts payable with accuracy and timelines.</p>
      </div>

      <h4 style={{ marginTop: "1.5rem" }}>Module Benefit</h4>
      <p className="muted">
        Build a strong foundation in practical accounting and step confidently into
        entry-level accounting and KPO roles. This module reflects real business
        accounting processes and professional workplace standards.
      </p>

      <h4>Learning Outcomes</h4>
      <ul className="topics-list">
        <li>Record daily transactions and prepare vouchers & invoices</li>
        <li>Perform ageing, bank, and ledger reconciliations</li>
        <li>Support depreciation, inventory & books finalization</li>
        <li>Understand KPO workflows for invoicing and accounts payable</li>
      </ul>
    </>
  )}

  {/* ================= INCOME TAX DETAILED CONTENT ================= */}
{course.title === "Income Tax" && (
  <>
    <h4 style={{ marginTop: "1.5rem" }}>Topic Explanation</h4>

    <div className="topic-explain">
      <p>
        <strong>Overview of Direct Taxation System:</strong> Direct tax is paid
        directly by individuals or businesses to the government, mainly through
        Income Tax. It is governed by laws that decide who pays tax, on which
        income, and how it is collected.
      </p>

      <p>
        <strong>Income Tax Act, 1961:</strong> Provides the legal framework for
        taxation in India and defines key terms like assessee, previous year,
        assessment year, income, and total income.
      </p>

      <p>
        <strong>Residential Status & PAN:</strong> Residential status determines
        taxability of income in India. PAN is a mandatory tax identity required
        for return filing, TDS credit, and financial transactions.
      </p>

      <p>
        <strong>Income Tax Registration:</strong> Enables online return filing,
        compliance, responding to notices, and tax-related activities through
        the Income Tax portal.
      </p>

      <p>
        <strong>Exempt Incomes:</strong> Certain incomes are fully or partially
        exempt under the Act, helping reduce taxable income legally.
      </p>

      <p>
        <strong>Income Heads:</strong> Income is classified under salary, house
        property, business/profession, capital gains, and other sources—each
        having specific computation rules.
      </p>

      <p>
        <strong>Capital Gains & Other Sources:</strong> Capital gains arise from
        selling assets like property or shares, while other sources include
        interest, dividends, and winnings.
      </p>

      <p>
        <strong>Deductions:</strong> Deductions reduce taxable income based on
        eligible investments and expenses, helping lower tax liability.
      </p>

      <p>
        <strong>Clubbing & Loss Set-Off:</strong> Clubbing prevents tax avoidance
        through income transfer, while loss set-off and carry forward allow
        adjustment against current or future income.
      </p>

      <p>
        <strong>Return Filing & Assessment:</strong> Filing returns reports
        income and taxes paid. Assessment is the department’s process of
        verifying and finalizing tax liability.
      </p>

      <p>
        <strong>Advance Tax:</strong> Tax paid in installments during the year
        when liability exceeds prescribed limits, avoiding interest penalties.
      </p>

      <p>
        <strong>Tax Computation:</strong> Taxable income is calculated differently
        for individuals and companies using specific provisions and rates.
      </p>

      <p>
        <strong>Minimum Alternate Tax (MAT):</strong> Ensures companies pay a
        minimum tax even when taxable income is reduced due to exemptions.
      </p>

      <p>
        <strong>Revised Returns & Appeals:</strong> Allows correction of errors
        and legal remedy against assessment orders.
      </p>

      <p>
        <strong>Deferred Tax, Penalty & Interest:</strong> Deferred tax arises due
        to timing differences. Penalties and interest apply for defaults.
      </p>

      <p>
        <strong>Tax Planning & Case Studies:</strong> Legal planning techniques
        using deductions and exemptions with real-life scenarios.
      </p>

      <p>
        <strong>TDS / TCS:</strong> Tax collection at source mechanisms ensuring
        timely government revenue, including Form 16 and e-TDS returns.
      </p>
    </div>

    <h4 style={{ marginTop: "1.5rem" }}>Module Benefit</h4>
    <p className="muted">
      This module provides practical, job-ready knowledge of India’s Income Tax
      system—helping learners compute taxable income, plan taxes legally, manage
      advance tax and TDS/TCS compliance, and file accurate returns confidently.
    </p>

    <h4>Learning Outcomes</h4>
    <ul className="topics-list">
      <li>Understand Income Tax basics, PAN, residential status, and compliance</li>
      <li>Compute taxable income for individuals and companies accurately</li>
      <li>Handle return filing, assessments, revisions, and advance tax</li>
      <li>Work confidently with TDS/TCS, Form 16, and tax planning cases</li>
    </ul>
  </>
)}
{/* ================= GST DETAILED CONTENT ================= */}
{course.title === "Tally & GST" && (
  <>
    <h4 style={{ marginTop: "1.5rem" }}>Topic Explanation</h4>

    <div className="topic-explain">
      <p>
        <strong>GST Constitution & Definitions:</strong> GST is governed by
        constitutional provisions and GST laws defining terms such as supply,
        goods, services, and taxable person—forming the foundation of GST
        compliance.
      </p>

      <p>
        <strong>Levy & Supply:</strong> GST is levied on the supply of goods or
        services. Understanding supply determines tax applicability and rates.
      </p>

      <p>
        <strong>Time, Place & Value of Supply:</strong> Time of supply determines
        when tax is payable, valuation decides taxable value, and place of supply
        decides CGST/SGST or IGST applicability.
      </p>

      <p>
        <strong>Input Tax Credit (ITC):</strong> ITC allows credit of GST paid on
        purchases subject to eligibility, reconciliation, blocked credits, and
        reversals.
      </p>

      <p>
        <strong>GST Registration:</strong> Mandatory once turnover crosses limits
        or for specified persons. Cancellation and revocation manage compliance
        lifecycle.
      </p>

      <p>
        <strong>Return Filing:</strong> Composition, monthly, and QRMP schemes
        apply based on turnover and business type.
      </p>

      <p>
        <strong>Return Filing, Computation & Payment:</strong> Covers preparation
        of returns, output tax calculation, ITC adjustment, and GST payment.
      </p>

      <p>
        <strong>Return Reconciliation:</strong> Matches books with GST returns to
        identify missing invoices, wrong GSTINs, and rate differences.
      </p>

      <p>
        <strong>Invoicing & E-Way Bill:</strong> Includes GST invoice formats,
        HSN/SAC, e-invoicing, job-work rules, and e-way bill compliance.
      </p>

      <p>
        <strong>PMT-09 & DRC-03:</strong> PMT-09 shifts balances between GST
        ledgers; DRC-03 is used for voluntary tax payments and compliance.
      </p>

      <p>
        <strong>Refunds & Annual Return:</strong> Covers excess tax/ITC refunds,
        annual return filing, and linking GSTR-1 with GSTR-3B.
      </p>

      <p>
        <strong>GST Audit & Advance Ruling:</strong> Audit verifies compliance,
        assessments determine liability, and advance ruling clarifies tax
        treatment.
      </p>

      <p>
        <strong>Inspection & Notices:</strong> Includes inspection, search,
        seizure, and handling show cause notices (SCN).
      </p>

      <p>
        <strong>GST on Stock Transfer & Works Contract:</strong> Special rules
        apply for branch transfers and works contracts affecting tax and ITC.
      </p>

      <p>
        <strong>Mismatch Reports, Penalty & Interest:</strong> Identifies
        differences between returns and books and explains penalty and interest
        provisions.
      </p>
    </div>

    <h4 style={{ marginTop: "1.5rem" }}>Module Benefit</h4>
    <p className="muted">
      This module builds practical GST expertise to handle end-to-end compliance—
      registration, invoicing, ITC management, return filing, reconciliation,
      payments, refunds, and notice handling—enabling confident GST handling in
      accounting firms and KPO roles.
    </p>

    <h4>Learning Outcomes</h4>
    <ul className="topics-list">
      <li>Understand GST basics including levy, supply, valuation, and place of supply</li>
      <li>Manage ITC eligibility and perform return reconciliation</li>
      <li>Handle GST registration, invoicing, e-invoicing, and e-way bills</li>
      <li>Perform GST return filing, payments, refunds, and basic notice handling</li>
    </ul>
  </>
)}
{/* ================= INTERVIEW SKILLS DETAILED CONTENT ================= */}
{course.title === "Interview Skills" && (
  <>
    <h4 style={{ marginTop: "1.5rem" }}>Topic Explanation</h4>

    <div className="topic-explain">
      <p>
        <strong>Resume Preparation:</strong> Learn how to create a clear,
        professional, and ATS-friendly resume using practical templates. Focus
        on highlighting your skills, projects, and experience aligned with job
        descriptions.
      </p>

      <p>
        <strong>Communication & Presentation Skills:</strong> Improve spoken and
        written communication for workplace situations. Learn how to express
        ideas confidently, clearly, and professionally.
      </p>

      <p>
        <strong>Mock Interviews (Technical & HR):</strong> Practice real-life
        interview scenarios to understand common technical and HR questions and
        how to answer them effectively.
      </p>

      <p>
        <strong>Group Discussion Techniques:</strong> Learn how to participate
        confidently in group discussions, stay relevant to the topic, listen
        actively, and handle disagreements politely.
      </p>

      <p>
        <strong>Confidence Building & Body Language:</strong> Build confidence
        through practical exercises and improve posture, eye contact, facial
        expressions, and gestures to appear professional and confident.
      </p>

      <p>
        <strong>Behavioral Questions (STAR Method):</strong> Learn the STAR
        method—Situation, Task, Action, Result—to answer behavioral interview
        questions in a structured and impactful way.
      </p>

      <p>
        <strong>Professional Etiquette:</strong> Understand workplace manners
        including email etiquette, meeting behavior, professional conduct, and
        effective communication with seniors, clients, and colleagues.
      </p>
    </div>

    <h4 style={{ marginTop: "1.5rem" }}>Module Benefit</h4>
    <p className="muted">
      This module prepares you for job interviews and workplace environments by
      improving resume quality, communication skills, confidence, and interview
      performance—helping you present yourself professionally and increase your
      chances of selection.
    </p>

    <h4>Learning Outcomes</h4>
    <ul className="topics-list">
      <li>Create a strong, job-ready resume using practical templates</li>
      <li>Improve communication, presentation, and workplace etiquette</li>
      <li>Practice HR and technical interviews using the STAR method</li>
      <li>Build confidence, body language, and group discussion skills</li>
    </ul>
  </>
)}
{/* ================= BANKING & AUDIT DETAILED CONTENT ================= */}
{course.title === "Banking & Audit" && (
  <>
    <h4 style={{ marginTop: "1.5rem" }}>Topic Explanation</h4>

    <div className="topic-explain">
      <p>
        <strong>Banking Basics & Reconciliation:</strong> Understand banking
        transactions such as deposits, withdrawals, charges, and interest.
        Learn bank reconciliation to match bank statements with book entries
        and identify differences like pending cheques or bank charges.
      </p>

      <p>
        <strong>ESI & PF Compliance Overview:</strong> Get an overview of
        statutory employee compliances like Provident Fund (PF) and Employee
        State Insurance (ESI), including contribution structure, due dates,
        and basic filing requirements.
      </p>

      <p>
        <strong>Audit Basics:</strong> Learn what an audit is, why it is
        conducted, and the main types such as internal audit, statutory audit,
        and tax audit. Understand audit scope and reporting practices.
      </p>

      <p>
        <strong>Internal Controls & Statutory Registers:</strong> Understand
        internal control systems used to prevent errors and fraud. Learn about
        statutory registers and their importance for compliance and audits.
      </p>

      <p>
        <strong>Cheque & Payment Advice Processes:</strong> Learn the correct
        method of cheque preparation and payment advice creation with
        invoice-wise details to ensure accurate payments and a proper audit
        trail.
      </p>

      <p>
        <strong>Practical Bank & Ledger Audit:</strong> Work on real examples
        of bank and ledger audits to verify accuracy, detect mismatches, and
        resolve reconciliation issues.
      </p>

      <p>
        <strong>Statutory Returns & Audit Working Papers:</strong> Understand
        common statutory returns and learn how audit working papers are
        prepared to support audit findings and conclusions.
      </p>
    </div>

    <h4 style={{ marginTop: "1.5rem" }}>Module Benefit</h4>
    <p className="muted">
      This module builds practical skills in banking operations, statutory
      compliance, and audit support—helping you perform reconciliations,
      maintain internal controls, prepare payment documentation, and assist
      in audits with confidence.
    </p>

    <h4>Learning Outcomes</h4>
    <ul className="topics-list">
      <li>Understand banking basics and perform bank reconciliation</li>
      <li>Learn PF & ESI compliance overview and documentation</li>
      <li>Understand audit fundamentals, internal controls & registers</li>
      <li>Prepare cheque/payment advice and support audits with working papers</li>
    </ul>
  </>
)}
{/* ================= MS EXCEL & REPORTING DETAILED CONTENT ================= */}
{course.title === "MS Excel & Reporting" && (
  <>
    <h4 style={{ marginTop: "1.5rem" }}>
      Benefits of Excel in Accounting & Financial Analysis
    </h4>

    <div className="topic-explain">
      <p>
        <strong>Accuracy & Speed in Calculations:</strong> Excel performs complex
        accounting calculations instantly and reduces manual errors compared to
        handwritten or calculator-based work. It is widely used for Profit & Loss,
        depreciation, interest, tax, and salary computations.
      </p>

      <p>
        <strong>Efficient Data Organization:</strong> Excel stores large volumes of
        accounting data in structured rows and columns. It is commonly used to
        maintain cash books, bank books, journals, ledgers, and trial balances using
        sorting, filtering, tables, and data validation.
      </p>

      <p>
        <strong>Powerful Financial Analysis:</strong> Excel helps accountants analyze
        business performance through ratio analysis, trend analysis, variance
        analysis, and budget vs actual comparison using Pivot Tables, Pivot Charts,
        and conditional formatting.
      </p>

      <p>
        <strong>Easy Reporting & MIS Creation:</strong> Excel is widely used for MIS
        reports such as monthly financial statements, expense analysis, revenue
        summaries, and department-wise cost reports. Reports can be customized,
        auto-updated, and exported to PDF or Word.
      </p>

      <p>
        <strong>Bank Reconciliation:</strong> Excel simplifies bank reconciliation by
        comparing bank statements with book entries. Functions like VLOOKUP /
        XLOOKUP, IF, and COUNTIF help identify unpresented cheques, bank charges,
        and interest credits.
      </p>

      <p>
        <strong>Automation Using Formulas & Functions:</strong> Excel supports
        automation using formulas such as SUM, AVERAGE, IF, VLOOKUP / XLOOKUP,
        COUNTIF, SUMIF, TEXT, DATE, and ROUND. Advanced users can automate tasks
        using Macros and VBA.
      </p>

      <p>
        <strong>Budgeting & Forecasting:</strong> Excel is used for preparing budgets,
        cash flow forecasts, expense planning, and sales projections. Scenario
        analysis helps evaluate best-case, worst-case, and expected outcomes.
      </p>

      <p>
        <strong>Audit & Compliance Support:</strong> Excel supports audit schedules,
        working papers, sample testing, and statutory compliance tracking such as
        PF, ESI, and GST basics. Excel-based documentation is widely accepted by
        auditors.
      </p>

      <p>
        <strong>Data Validation & Internal Control:</strong> Excel prevents errors by
        restricting data entry, creating dropdowns, locking formulas, and protecting
        sheets or workbooks—strengthening internal controls.
      </p>

      <p>
        <strong>Cost-Effective & Widely Accepted Tool:</strong> Excel does not require
        expensive accounting software for basic work and is used across CA firms,
        corporates, banks, startups, and government offices. Excel skills are
        mandatory for most accounting jobs.
      </p>
    </div>

    <h4 style={{ marginTop: "1.5rem" }}>Module Benefit</h4>
    <p className="muted">
      This module builds strong Excel skills for accounting, reporting, analysis,
      and automation—helping you work faster, reduce errors, and create
      professional financial reports used in real workplaces.
    </p>

    <h4>Learning Outcomes</h4>
    <ul className="topics-list">
      <li>Perform accurate accounting calculations using Excel formulas</li>
      <li>Organize and manage accounting data effectively</li>
      <li>Create MIS reports, dashboards, and financial summaries</li>
      <li>Automate repetitive tasks and support audits & compliance</li>
    </ul>
  </>
)}
        </div>
      </main>
    );
  }

  /* ========== COURSE LIST PAGE ========== */
return (
  <main
        className="courses-section courses-bg"
        style={{ backgroundImage: "url(/images/template.png)" }}
  >

    <div className="container">
      <div className="heading-strip">
        <div className="heading-box">
          <h1>JOB ORIENTED ACCOUNTING COURSES</h1>
        </div>
      </div>

      <header className="section-head">
        <h2>Certificate Program for Account Executive (CPAE)</h2>
        <p className="muted">
          Practical, job-oriented short courses for commerce graduates & beginners.
        </p>
      </header>

      <section className="cards-grid">
        {modules.map((m) => (
          <article key={m.id} className="course-card">
            
            {/* IMAGE */}
            <div className="card-media">
              <img src={m.cardImage} alt={m.title} />
            </div>

            {/* CONTENT */}
            <div className="card-body">
              <h3>{m.title}</h3>
              <p className="card-sub">{m.subtitle}</p>

              {/* 2–3 LINE DESCRIPTION */}
              <p className="card-desc">{m.shortDesc}</p>

              {/* NAVIGATION */}
              <Link to={`/courses/${m.id}`} className="btn btn-blue">
                Know More
              </Link>
            </div>

          </article>
        ))}
      </section>

    </div>
  </main>
 );
}